import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import Masthead from "@/components/alcott/Masthead";
import Seo from "@/components/Seo";
import AlcottFooter from "@/components/alcott/AlcottFooter";
import HorizonWaitlist from "@/components/alcott/HorizonWaitlist";
import PageHero from "@/components/alcott/PageHero";
import { ESSAYS, ARCHIVE_SPIRITUAL, ARCHIVE_FASHION, ARCHIVE_ART_CITIES, NIIJI_ESSAYS } from "@/data/alcott";

const ease = [0.22, 1, 0.36, 1];

const ArchiveInner = ({ a }) => (
  <>
    <div className="relative aspect-[4/5] overflow-hidden rounded-sm">
      <img src={a.image} alt={a.title} className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
    </div>
    <p className="mt-5 font-body text-[0.62rem] uppercase tracking-[0.22em] text-[#C05C42]">{a.category}</p>
    <h3 className="mt-2 font-serif text-2xl font-semibold leading-tight tracking-tight">{a.title}</h3>
    <p className="mt-2 font-body text-sm leading-relaxed text-[#0A0A0A]/60">{a.excerpt}</p>
    <p className="mt-3 font-body text-[0.62rem] uppercase tracking-[0.18em] text-[#0A0A0A]/40">
      {a.author} · {a.readTime}
    </p>
  </>
);

const ArchiveSection = ({ id, overline, title, blurb, items }) => (
  <div id={id} className="mt-20 scroll-mt-28 border-t border-[#0A0A0A]/12 pt-12" data-testid={`archive-section-${id}`}>
    <div className="mb-10">
      <p className="font-body text-[0.62rem] uppercase tracking-[0.25em] text-[#C05C42]">{overline}</p>
      <h2 className="mt-3 font-serif text-[clamp(1.9rem,4vw,3rem)] font-semibold tracking-tight">{title}</h2>
      {blurb && <p className="mt-3 max-w-2xl font-body text-sm leading-relaxed text-[#0A0A0A]/55">{blurb}</p>}
    </div>
    <div className="grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((a, i) => (
        <motion.div
          key={a.id}
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-8%" }}
          transition={{ duration: 0.7, delay: (i % 3) * 0.06, ease }}
          data-testid={`archive-${a.id}`}
        >
          {a.internal ? (
            <Link to={`/essay/${a.slug}`} className="group block">
              <ArchiveInner a={a} />
            </Link>
          ) : (
            <a href={a.url} target="_blank" rel="noreferrer" className="group block">
              <ArchiveInner a={a} />
            </a>
          )}
        </motion.div>
      ))}
    </div>
  </div>
);

export const Archives = () => {
  const [waitlist, setWaitlist] = useState(false);
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const scroll = () => {
      const el = document.getElementById(id);
      if (!el) return;
      if (window.lenis) window.lenis.scrollTo(el, { offset: -110 });
      else el.scrollIntoView({ behavior: "smooth" });
    };
    // Two passes: first after mount, second after images reflow the layout.
    const t1 = setTimeout(scroll, 450);
    const t2 = setTimeout(scroll, 1300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [hash]);
  return (
    <div className="min-h-screen bg-[#FAF9F6] font-body text-[#0A0A0A]" data-testid="archives-page">
      <Seo title="Society Archives" description="Recovered correspondence, salon records, and cultural essays — heritage, art, fashion, and the sacred, annotated by the editors of Sybils & Society." />
      <Masthead />

      <PageHero
        overline="The Society Archive"
        title="History, Spirit & Heritage"
        blurb="The living record of unique Black American heritage — modern spirituality, heritage fashion, and the art and cities that shaped us. Essays by Veronica Bey. The deepest archive is held within Sybils Society."
      />

      <section className="mx-auto max-w-[1500px] px-5 pb-16 md:px-10 md:pb-24">
        <ArchiveSection
          id="spirit"
          overline="Modern Spirit"
          title="The Living Faith"
          blurb="Devotion on our own terms — ancestor work, ritual, and the altar within."
          items={ARCHIVE_SPIRITUAL}
        />
        <ArchiveSection
          id="fashion"
          overline="Heritage & Fashion"
          title="What We Wore, What We Wove"
          blurb="Historical dress and adornment as the record of a self-authoring people."
          items={ARCHIVE_FASHION}
        />
        <ArchiveSection
          id="ledger"
          overline="The Ledger · in partnership with Niiji Capital"
          title="Culture Meets Capital"
          blurb="Cross-brand features on Niiji Capital's sovereign-aligned ventures — maritime mobility, conservation, and land held in trust."
          items={NIIJI_ESSAYS}
        />
        <div className="mt-10 flex flex-col items-start gap-4 rounded-xl border border-[#0A0A0A]/12 bg-[#F4EFE6] px-6 py-8 sm:flex-row sm:items-center sm:justify-between" data-testid="ledger-horizon-cta">
          <div>
            <p className="font-body text-[0.62rem] uppercase tracking-[0.22em] text-[#C05C42]">Where culture meets global mobility</p>
            <h3 className="mt-2 font-serif text-2xl font-semibold tracking-tight md:text-3xl">Continue into The Horizon Circle</h3>
            <p className="mt-2 max-w-xl font-body text-sm leading-relaxed text-[#0A0A0A]/60">
              These ventures live within our elite travel and lifestyle network — mobility as sovereignty, held in trust.
            </p>
          </div>
          <a
            href="https://thehorizoncircle.co"
            target="_blank"
            rel="noreferrer"
            className="shrink-0 rounded-full bg-[#0A0A0A] px-7 py-3 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#FAF9F6] transition-transform duration-300 hover:-translate-y-0.5"
            data-testid="ledger-horizon-link"
          >
            Enter The Horizon Circle
          </a>
        </div>
        <ArchiveSection
          id="art-cities"
          overline="Art & Cities"
          title="Where Geography Became Genius"
          blurb="Black American art and the cities across the United States that raised it."
          items={ARCHIVE_ART_CITIES}
        />
        <ArchiveSection
          id="history"
          overline="The Cultural Digest · from findyatribe.org"
          title="What's Tea?"
          blurb="The cultural digest — discourse, ancestry, and the stories worth passing on: language revival, reclaimed lineage, and what the culture is saying now."
          items={ESSAYS}
        />

        {/* Gated deep archive */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
          className="mt-24 flex flex-col items-center rounded-2xl border border-[#0A0A0A]/15 bg-[#F4EFE6] px-6 py-16 text-center"
          data-testid="archives-gated"
        >
          <Lock size={26} strokeWidth={1.25} className="text-[#C05C42]" />
          <h2 className="mt-6 font-serif text-3xl font-semibold tracking-tight md:text-4xl">The Deep Archive is sealed</h2>
          <p className="mt-4 max-w-md font-body text-sm leading-relaxed text-[#0A0A0A]/60">
            Ciphers, ancestry research, rare literary pieces, and confidential salons are
            reserved for verified members of Sybils Society.
          </p>
          <Link
            to="/veil"
            onClick={() => sessionStorage.removeItem("sybil_entered")}
            className="mt-8 rounded-full bg-[#0A0A0A] px-7 py-3 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#FAF9F6] transition-transform duration-300 hover:-translate-y-0.5"
            data-testid="archives-enter-society-btn"
          >
            Enter Sybils Society
          </Link>
        </motion.div>
      </section>

      <AlcottFooter onJoin={() => setWaitlist(true)} />
      <HorizonWaitlist open={waitlist} onClose={() => setWaitlist(false)} variant="outer" />
    </div>
  );
};

export default Archives;
